import { runInAction } from "mobx";
import { PropertyBrowserNodeOUT } from "api-client";

export interface NodePage {
  items: PropertyBrowserNodeOUT[];
  hasMore: boolean;
  page: number;
}

export const PAGE_SIZE = 50;

export abstract class PropertyBrowserBaseStore {
  nodeChildren: Map<string | null, NodePage> = new Map();
  loadingNodes: Set<string | null> = new Set();
  searchQuery = "";
  searchResults: PropertyBrowserNodeOUT[] = [];
  searchLoading = false;
  searchTooGeneral = false;

  protected abstract fetchPage(
    parentNodeId: string | null,
    page: number
  ): Promise<{ items: PropertyBrowserNodeOUT[]; has_more: boolean } | null>;

  protected abstract fetchSearchResults(
    query: string,
    parentNodeId?: string
  ): Promise<PropertyBrowserNodeOUT[]>;

  reset() {
    this.nodeChildren = new Map();
    this.loadingNodes = new Set();
    this.searchQuery = "";
    this.searchResults = [];
    this.searchLoading = false;
    this.searchTooGeneral = false;
  }

  async fetchChildren(parentNodeId: string | null) {
    if (this.loadingNodes.has(parentNodeId)) return;
    if (this.nodeChildren.has(parentNodeId)) return;

    runInAction(() => this.loadingNodes.add(parentNodeId));
    try {
      const data = await this.fetchPage(parentNodeId, 1);
      if (!data) return;
      runInAction(() => {
        this.nodeChildren.set(parentNodeId, {
          items: data.items,
          hasMore: data.has_more,
          page: 1,
        });
      });
    } catch (err) {
      console.error("PropertyBrowserStore.fetchChildren", err);
    } finally {
      runInAction(() => this.loadingNodes.delete(parentNodeId));
    }
  }

  async fetchMore(parentNodeId: string | null) {
    if (this.loadingNodes.has(parentNodeId)) return;
    const existing = this.nodeChildren.get(parentNodeId);
    if (!existing?.hasMore) return;

    const nextPage = existing.page + 1;
    runInAction(() => this.loadingNodes.add(parentNodeId));
    try {
      const data = await this.fetchPage(parentNodeId, nextPage);
      if (!data) return;
      runInAction(() => {
        this.nodeChildren.set(parentNodeId, {
          items: [...existing.items, ...data.items],
          hasMore: data.has_more,
          page: nextPage,
        });
      });
    } catch (err) {
      console.error("PropertyBrowserStore.fetchMore", err);
    } finally {
      runInAction(() => this.loadingNodes.delete(parentNodeId));
    }
  }

  async search(query: string, parentNodeId?: string) {
    runInAction(() => {
      this.searchQuery = query;
      this.searchLoading = true;
      this.searchTooGeneral = false;
    });
    if (!query) {
      runInAction(() => {
        this.searchResults = [];
        this.searchLoading = false;
      });
      return;
    }
    try {
      const items = await this.fetchSearchResults(query, parentNodeId);
      runInAction(() => {
        this.searchResults = items;
      });
    } catch (err: any) {
      if (err?.response?.status === 422) {
        runInAction(() => (this.searchTooGeneral = true));
      } else {
        console.error("PropertyBrowserStore.search", err);
      }
    } finally {
      runInAction(() => (this.searchLoading = false));
    }
  }

  findNodeByColumnKey(columnKey: string): PropertyBrowserNodeOUT | undefined {
    for (const page of this.nodeChildren.values()) {
      const found = page.items.find((n) => n.column_key === columnKey);
      if (found) return found;
    }
    return undefined;
  }

  async fetchAllChildren() {
    const visited = new Set<string | null>();
    const queue: Array<string | null> = [null];

    while (queue.length > 0) {
      const batch = queue.splice(0);
      await Promise.all(
        batch
          .filter((id) => !visited.has(id))
          .map(async (parentId) => {
            visited.add(parentId);
            await this.fetchChildren(parentId);
            const page = this.nodeChildren.get(parentId);
            page?.items
              .filter((n) => n.has_children && !visited.has(n.node_id))
              .forEach((n) => queue.push(n.node_id));
          })
      );
    }
  }
}
